import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Lottie from 'lottie-react';
import bgImage from '../images/background.png';
import waveDarkAnim from '../animations/wave-dark.json';
import waveLightAnim from '../animations/wave-light.json';
import WineA from '../images/wine-a.png';
import WineB from '../images/wine-b.png';
import WineC from '../images/wine-c.png';
import WineD from '../images/wine-d.png';
import WineE from '../images/wine-e.png';
import WineF from '../images/wine-f.png';
import WineG from '../images/wine-g.png';
import WineH from '../images/wine-h.png';
import WineI from '../images/wine-i.png';
import points from '../images/puntuacion.png';
import pointsEn from '../images/puntuacion-en.jpg';

const WINE_IMAGES = [WineA, WineB, WineC, WineD, WineE, WineF, WineG, WineH, WineI];
const WINE_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

function CwIcon({ name, size = 20 }) {
    const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
    switch (name) {
        case 'home':
            return <svg {...props}><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M10 20v-6h4v6" /></svg>;
        case 'back':
            return <svg {...props}><path d="M15 18l-6-6 6-6" /></svg>;
        case 'help':
            return <svg {...props}><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>;
        case 'x':
            return <svg {...props}><path d="M18 6L6 18" /><path d="M6 6l12 12" /></svg>;
        default: return null;
    }
}

export default function ChooseComponent() {
    const { t, i18n } = useTranslation();
    const [testsData, setTestsData] = useState([]);
    const [profileInfo, setProfileInfo] = useState({ id: 0 });
    const [isHelpPopupVisible, setHelpPopupVisible] = useState(false);
    const [hintsClicked, setHintsClicked] = useState(false);
    const [ready, setReady] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const [waveFalling, setWaveFalling] = useState(() => sessionStorage.getItem('waveIn') === '1');
    const lottieRef = useRef(null);
    const navigate = useNavigate();
    const theme = document.documentElement.getAttribute('data-theme') || 'dark';
    const waveAnim = theme === 'dark' ? waveDarkAnim : waveLightAnim;

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userLogged');
        if (storedUserInfo) setProfileInfo(JSON.parse(storedUserInfo));
        requestAnimationFrame(() => setReady(true));

        // Si venimos del play, la ola ya cubre la pantalla → bajarla
        if (sessionStorage.getItem('waveIn')) {
            sessionStorage.removeItem('waveIn');
        }
    }, []);

    // Arrancar la ola en reversa desde el último frame una vez el ref está listo
    useEffect(() => {
        if (!waveFalling || !lottieRef.current) return;
        const lottie = lottieRef.current;
        const total = lottie.totalFrames;
        lottie.goToAndStop(total - 1, true);
        lottie.setDirection(-1);
        lottie.play();
    }, [waveFalling]);

    const clearSessionData = () => {
        ['currentTest','selected0','selected1','selected2','selected3','selected4','selected5','selected6']
            .forEach(key => localStorage.removeItem(key));
    };

    const checkForCurrentTest = () => {
        const currentTest = localStorage.getItem('currentTest');
        if (currentTest) {
            const parsedCurrentTest = JSON.parse(currentTest);
            const xhr = new XMLHttpRequest();
            xhr.open("GET", `${process.env.REACT_APP_API_URL}/getTests/${profileInfo.id}`, true);
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4 && xhr.status === 200) {
                    const data = JSON.parse(xhr.responseText);
                    if (data.some(test => test.id === parsedCurrentTest.id)) navigate('/test');
                    else clearSessionData();
                }
            };
            xhr.send();
        }
    };

    useEffect(() => {
        if (profileInfo.id) checkForCurrentTest();
    }, [profileInfo]);

    useEffect(() => {
        if (!profileInfo.id) return;
        setFetchError(false);
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/getTests/${profileInfo.id}`, true);
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                if (xhr.status === 200) setTestsData(JSON.parse(xhr.responseText));
                else if (xhr.status === 204) navigate('/wine-order');
                else if (xhr.status === 205) navigate('/choose-answer-alert');
                // else if (xhr.status === 403) navigate('/subscription-alert'); // desactivado temporalmente
                else if (xhr.status === 203) navigate('/game-alert');
                else setFetchError(true);
            }
        };
        xhr.send();
    }, [profileInfo, retryKey]);

    const handleWineClick = (e, wineTitle) => {
        const selectedTest = testsData.find(test => test.title === wineTitle);
        if (selectedTest) localStorage.setItem('currentTest', JSON.stringify(selectedTest));
        else e.preventDefault();
    };

    const sortedTests = testsData
        .slice()
        .sort((a, b) => WINE_LABELS.indexOf(a.title) - WINE_LABELS.indexOf(b.title));

    return (
        <div className={`cw-stage${ready ? ' cw-ready' : ''}`}>
            <div className="cw-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="cw-vignette" />

            {/* Ola de transición — baja para revelar esta pantalla */}
            {waveFalling && (
                <div className="wave-transition-overlay">
                    <Lottie
                        animationData={waveAnim}
                        lottieRef={lottieRef}
                        autoplay={false}
                        loop={false}
                        speed={2.5}
                        onComplete={() => setWaveFalling(false)}
                        rendererSettings={{ preserveAspectRatio: 'xMidYMax slice' }}
                        style={{ width: '100%', height: '100%' }}
                    />
                </div>
            )}
            <div className="cw-grain" />

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>{t('common.retry')}</button>
                </div>
            )}

            {/* Topbar — solo título */}
            <div className="cw-topbar">
                <div className="cw-topbar-title"></div>
            </div>

            {/* Botellas */}
            <div className="cw-content">
                <div className="cw-panel">
                <div className="cw-heading">
                    <h2 className="cw-heading-text">{t('choose_wine.title')}</h2>
                </div>
                <div className={`cw-bottles cw-bottles-${Math.max(sortedTests.length, 2)}`}>
                    {sortedTests.map((test, index) => (
                        <Link
                            key={test.title}
                            to="/test"
                            className="cw-bottle-link"
                            onClick={(e) => handleWineClick(e, test.title)}
                            style={{ animationDelay: `${index * 60}ms` }}
                        >
                            <div className="cw-bottle-wrap">
                                <img src={WINE_IMAGES[index % WINE_IMAGES.length]} alt={`${t('choose_wine.wine')} ${test.title}`} className="cw-bottle-img" />
                                <div className="cw-bottle-shine" />
                                <div className="cw-bottle-glow" />
                            </div>
                            <div className="cw-bottle-label">{test.title}</div>
                        </Link>
                    ))}
                </div>
                </div>
            </div>

            {/* Bottom nav */}
            <nav className="cw-bottom-nav">
                <Link to="/" className="cw-bottom-btn" aria-label={t('common.home')}>
                    <CwIcon name="home" size={22} />
                    <span>{t('common.home')}</span>
                </Link>
                <button className="cw-bottom-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <CwIcon name="back" size={22} />
                    <span>{t('common.back')}</span>
                </button>
                <button className={`cw-bottom-btn${hintsClicked ? '' : ' cw-bottom-hints'}`} onClick={() => { setHintsClicked(true); setHelpPopupVisible(true); }} aria-label={t('common.help')}>
                    <CwIcon name="help" size={22} />
                    <span>{t('common.hints')}</span>
                </button>
            </nav>

            {/* Popup ayuda */}
            {isHelpPopupVisible && (
                <div className="cw-overlay" onClick={() => setHelpPopupVisible(false)}>
                    <div className="cw-popup" onClick={e => e.stopPropagation()}>
                        <button className="cw-popup-close" onClick={() => setHelpPopupVisible(false)}>
                            <CwIcon name="x" size={18} />
                        </button>
                        <img src={i18n.language === 'en' ? pointsEn : points} alt={t('common.help')} className="cw-popup-img" />
                    </div>
                </div>
            )}
        </div>
    );
}
